import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * Obsidian Pro login surface.
 * Auth only — no market math.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh, user, loading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      const next = params.get('next') || '/';
      navigate(next.startsWith('/') ? next : '/', { replace: true });
    }
  }, [loading, user, params, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d.error || `Login failed (${r.status})`);
        return;
      }
      await refresh();
      const next = params.get('next') || '/';
      navigate(next.startsWith('/') ? next : '/', { replace: true });
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tmr-login-v2">
      <div className="tmr-login-v2__grid" aria-hidden="true" />
      <div className="tmr-login-v2__card">
        <div className="tmr-login-v2__brand">
          <div className="tmr-login-v2__mark">
            <i className="bi bi-radar" />
          </div>
          <h1>Tactical Market Radar</h1>
          <p>Institutional EOD &amp; live analysis terminal</p>
        </div>

        <form className="tmr-login-v2__form" onSubmit={onSubmit} autoComplete="on">
          <label className="tmr-login-v2__field">
            <span>Username</span>
            <div className="tmr-login-v2__input">
              <i className="bi bi-person" aria-hidden="true" />
              <input
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                placeholder="Enter username"
              />
            </div>
          </label>

          <label className="tmr-login-v2__field">
            <span>Password</span>
            <div className="tmr-login-v2__input">
              <i className="bi bi-lock" aria-hidden="true" />
              <input
                type={showPw ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter password"
              />
              <button
                type="button"
                className="tmr-login-v2__eye"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                <i className={`bi bi-eye${showPw ? '-slash' : ''}`} />
              </button>
            </div>
          </label>

          {error ? (
            <div className="tmr-login-v2__error" role="alert">
              <i className="bi bi-exclamation-triangle-fill" /> {error}
            </div>
          ) : null}

          <button type="submit" className="tmr-login-v2__btn" disabled={busy}>
            {busy ? (
              <>
                <span className="tmr-loading-orb" style={{ width: 16, height: 16, borderWidth: 2 }} />
                Signing in…
              </>
            ) : (
              <>
                Sign in <i className="bi bi-arrow-right" />
              </>
            )}
          </button>
        </form>

        <div className="tmr-login-v2__foot">
          <span className="tmr-login-v2__pill">Obsidian Pro</span>
          <span>React · Vite · PM2</span>
        </div>
      </div>

      <style>{`
        .tmr-login-v2 {
          min-height: 100vh; display: grid; place-items: center; padding: 24px;
          position: relative; overflow: hidden;
          background: #050506;
          font-family: var(--font-sans, Inter, system-ui, sans-serif);
        }
        .tmr-login-v2__grid {
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(900px 500px at 15% -10%, rgba(0,240,255,.14), transparent 55%),
            radial-gradient(700px 420px at 90% 10%, rgba(124,58,237,.16), transparent 50%),
            radial-gradient(500px 300px at 50% 110%, rgba(0,255,102,.06), transparent 50%),
            linear-gradient(rgba(255,255,255,.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.02) 1px, transparent 1px);
          background-size: auto, auto, auto, 48px 48px, 48px 48px;
        }
        .tmr-login-v2__card {
          position: relative; z-index: 1;
          width: min(420px, 100%);
          padding: 32px 28px 22px;
          border-radius: 20px;
          background: rgba(12,12,16,.78);
          border: 1px solid rgba(255,255,255,.10);
          box-shadow: 0 30px 80px rgba(0,0,0,.55), 0 0 0 1px rgba(0,240,255,.06), inset 0 1px 0 rgba(255,255,255,.06);
          backdrop-filter: blur(18px) saturate(1.2);
        }
        .tmr-login-v2__brand { text-align: center; margin-bottom: 26px; }
        .tmr-login-v2__mark {
          width: 56px; height: 56px; margin: 0 auto 14px; border-radius: 16px;
          display: grid; place-items: center; font-size: 24px; color: #050506;
          background: linear-gradient(135deg, #00F0FF, #7C3AED);
          box-shadow: 0 0 32px rgba(0,240,255,.28);
        }
        .tmr-login-v2__brand h1 {
          margin: 0; font-size: 1.35rem; font-weight: 800; letter-spacing: -0.02em; color: #F5F7FA;
        }
        .tmr-login-v2__brand p {
          margin: 8px 0 0; font-size: 12px; font-weight: 600; color: rgba(245,247,250,.52);
        }
        .tmr-login-v2__form { display: flex; flex-direction: column; gap: 14px; }
        .tmr-login-v2__field { display: flex; flex-direction: column; gap: 6px; }
        .tmr-login-v2__field > span {
          font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase;
          color: rgba(245,247,250,.48);
        }
        .tmr-login-v2__input {
          display: flex; align-items: center; gap: 8px;
          height: 44px; padding: 0 12px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,.10);
          background: rgba(0,0,0,.35);
          transition: border-color .15s, box-shadow .15s;
        }
        .tmr-login-v2__input:focus-within {
          border-color: rgba(0,240,255,.55);
          box-shadow: 0 0 0 3px rgba(0,240,255,.12);
        }
        .tmr-login-v2__input i { color: rgba(0,240,255,.7); font-size: 15px; }
        .tmr-login-v2__input input {
          flex: 1; border: none; outline: none; background: transparent;
          color: #F5F7FA; font-size: 14px; font-weight: 600; font-family: inherit;
          min-width: 0;
        }
        .tmr-login-v2__input input::placeholder { color: rgba(245,247,250,.32); font-weight: 500; }
        .tmr-login-v2__eye {
          border: none; background: transparent; color: rgba(245,247,250,.4);
          cursor: pointer; padding: 4px; display: grid; place-items: center;
        }
        .tmr-login-v2__eye:hover { color: #00F0FF; }
        .tmr-login-v2__btn {
          margin-top: 4px; height: 46px; border: none; border-radius: 12px; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          font-weight: 800; font-size: 14px; font-family: inherit;
          background: linear-gradient(90deg, #00F0FF, #38bdf8);
          color: #050506;
          box-shadow: 0 0 28px rgba(0,240,255,.22);
        }
        .tmr-login-v2__btn:disabled { opacity: .65; cursor: wait; }
        .tmr-login-v2__btn:hover:not(:disabled) { filter: brightness(1.05); }
        .tmr-login-v2__error {
          display: flex; align-items: center; gap: 8px;
          font-size: 12px; font-weight: 700; color: #fca5a5;
          background: rgba(255,45,106,.10); border: 1px solid rgba(255,45,106,.28);
          border-radius: 10px; padding: 10px 12px;
        }
        .tmr-login-v2__foot {
          margin-top: 20px; display: flex; align-items: center; justify-content: center; gap: 10px;
          font-size: 11px; font-weight: 600; color: rgba(245,247,250,.32);
        }
        .tmr-login-v2__pill {
          padding: 3px 8px; border-radius: 999px;
          border: 1px solid rgba(0,240,255,.25); color: #00F0FF;
          background: rgba(0,240,255,.08); letter-spacing: .06em; text-transform: uppercase;
          font-size: 9px; font-weight: 800;
        }
      `}</style>
    </div>
  );
}
